import React, { useEffect, useMemo, useState } from 'react';

export default function KpaModal({
                                     show,
                                     mode = 'create',                 // 'create' | 'edit'
                                     onClose,
                                     onSubmit,                         // (dto) => Promise
                                     timeframes = [],
                                     remaining = 90,                   // remaining capacity for CREATE
                                     initial = null,                   // KPA when editing
                                 }) {
    const isEdit = mode === 'edit';

    // form state
    const [name, setName] = useState('');
    const [weight, setWeight] = useState('');
    const [timeframeId, setTimeframeId] = useState('');
    const [alignment, setAlignment] = useState('');
    const [description, setDescription] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    // when modal opens, seed values
    useEffect(() => {
        if (!show) return;
        setError(null);
        setSubmitting(false);
        if (isEdit && initial) {
            setName(initial.name ?? '');
            setWeight(String(initial.weight ?? ''));
            setTimeframeId(initial.timeframe_id ?? initial.timeframe?.id ?? '');
            setAlignment(initial.alignment ?? '');
            setDescription(initial.description ?? '');
        } else {
            setName('');
            setWeight('');
            setTimeframeId('');
            setAlignment('');
            setDescription('');
        }
    }, [show, isEdit, initial]);

    const parsedWeight = useMemo(() => {
        const n = Number(weight);
        return Number.isFinite(n) ? n : 0;
    }, [weight]);

    // For EDIT, allow the user to reuse its own current weight
    const maxAllowed = useMemo(() => {
        if (!isEdit) return remaining;
        const current = Number(initial?.weight ?? 0);
        return Number(remaining) + current; // remaining capacity + current item’s weight
    }, [isEdit, remaining, initial]);

    const canSubmit = useMemo(() => {
        if (!name.trim()) return false;
        if (!Number.isFinite(parsedWeight) || parsedWeight <= 0) return false;
        if (parsedWeight > maxAllowed) return false;
        if (!alignment.trim()) return false;
        return true;
    }, [name, parsedWeight, maxAllowed, alignment]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setSubmitting(true);
        setError(null);
        try {
            const dto = {
                name: name.trim(),
                weight: parseFloat(parsedWeight.toFixed(2)),
                timeframe_id: timeframeId || null,
                alignment: alignment.trim(),
                description: description.trim() || null,
            };
            await onSubmit(dto);
            onClose();
        } catch (err) {
            const msg = err?.response?.data?.message || 'Failed to save KPA.';
            setError(msg);
        } finally {
            setSubmitting(false);
        }
    };

    if (!show) return null;

    return (
        <div className="modal d-block" tabIndex="-1" role="dialog" style={{ background: 'rgba(0,0,0,0.15)' }}>
            <div className="modal-dialog modal-lg" role="document">
                <form className="modal-content" onSubmit={handleSubmit}>
                    <div className="modal-header">
                        <h5 className="modal-title">{isEdit ? 'Edit KPA' : 'Add KPA'}</h5>
                        <button type="button" className="btn-close" aria-label="Close" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">
                        {error && <div className="alert alert-danger">{error}</div>}

                        <div className="row g-3">
                            <div className="col-md-7">
                                <label className="form-label">Name</label>
                                <input
                                    className="form-control"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="e.g., Customer Satisfaction"
                                    required
                                />
                            </div>

                            <div className="col-md-5">
                                <label className="form-label">
                                    Weight (≤ {Number(maxAllowed).toFixed(2)})
                                </label>
                                <input
                                    type="number"
                                    className={`form-control ${parsedWeight > maxAllowed ? 'is-invalid' : ''}`}
                                    value={weight}
                                    onChange={(e) => setWeight(e.target.value)}
                                    placeholder="e.g., 30"
                                    step="0.01"
                                    min="0"
                                    required
                                />
                                {parsedWeight > maxAllowed && (
                                    <div className="invalid-feedback">
                                        This exceeds the allowed {Number(maxAllowed).toFixed(2)}%
                                    </div>
                                )}
                            </div>

                            <div className="col-md-4">
                                <label className="form-label">Timeframe</label>
                                <select
                                    className="form-select"
                                    value={timeframeId}
                                    onChange={(e) => setTimeframeId(e.target.value)}
                                >
                                    <option value="">— Select —</option>
                                    {timeframes.map(tf => (
                                        <option key={tf.id} value={tf.id}>{tf.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="col-12">
                                <label className="form-label">Alignment</label>
                                <textarea
                                    className="form-control"
                                    rows={2}
                                    value={alignment}
                                    onChange={(e) => setAlignment(e.target.value)}
                                    placeholder="Explain alignment with business strategy…"
                                    required
                                />
                            </div>

                            <div className="col-12">
                                <label className="form-label">Description (optional)</label>
                                <textarea
                                    className="form-control"
                                    rows={3}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="More details, context, references…"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="modal-footer">
                        <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={submitting}>
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-primary" disabled={!canSubmit || submitting}>
                            {submitting ? 'Saving…' : (isEdit ? 'Save Changes' : 'Save KPA')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
